import { Action, Dispatch } from 'redux';

export const PAGE_OPEN_MAIN_TAB = 'PAGE_OPEN_MAIN_TAB';
export const PAGE_GO_PREV = 'PAGE_GO_PREV';
export const PAGE_GO_NEXT = 'PAGE_GO_NEXT';
export const PAGE_GO_TO = 'PAGE_GO_TO';
export const PAGE_OPEN_TAB = 'PAGE_OPEN_TAB';
export const PAGE_REPLACE_CONTENT = 'PAGE_REPLACE_CONTENT';
export const PAGE_CLOSE_TAB = 'PAGE_CLOSE_TAB';
export const PAGE_OPEN_REFLOWABLE = 'PAGE_OPEN_REFLOWABLE';
export const PAGE_OPEN_GENERATED_CONTENT = 'PAGE_OPEN_GENERATED_CONTENT';
export const PAGE_SELECT_TAB = 'PAGE_SELECT_TAB';
export const PAGE_OPEN_POPUP = 'PAGE_OPEN_POPUP';
export const PAGE_CLOSE_POPUP = 'PAGE_CLOSE_POPUP';
export const PAGE_SET_POPUP_COLLAPSED = 'PAGE_SET_POPUP_COLLAPSED';
export const PAGE_SET_FOCUS = 'PAGE_SET_FOCUS';
export const PAGE_GO_TO_REFERENCE = 'PAGE_GO_TO_REFERENCE';

interface SimplePageAction extends Action {
  readonly type: typeof PAGE_GO_PREV | typeof PAGE_GO_NEXT;
}
interface OpenMainTabAction extends Action {
  readonly type: typeof PAGE_OPEN_MAIN_TAB;
  readonly title: string;
  readonly pageIndex: number;
}
interface OpenTabAction extends Action {
  readonly type: typeof PAGE_OPEN_TAB;
  readonly key?: string;
  readonly path: string;
  readonly title: string;
  readonly favicon: string | null;
  readonly active: boolean;
}

// TODO : OpenTabActionがkey="self"をサポートすれば統合可能
interface ReplaceContentAction extends Action {
  readonly type: typeof PAGE_REPLACE_CONTENT;
  readonly path: string;
  readonly title: string;
  readonly favicon: string | null;
}

interface OpenReflowableAction extends Action {
  readonly type: typeof PAGE_OPEN_REFLOWABLE;
  readonly title: string;
  readonly pageIndex: number;
}
interface OpenGeneratedContentAction extends Action {
  readonly key: string;
  readonly type: typeof PAGE_OPEN_GENERATED_CONTENT;
  readonly location: 'tab'; // Popup対応は廃止した。あとでlocation自体削除する。
  readonly title: string;
  readonly xhtml: string;
  readonly params: any; // 汎用の追加パラメータ、現状は固定レイアウト動的生成時にwidth, heightを入れている。
}
interface ClosePageAction extends Action {
  readonly type: typeof PAGE_CLOSE_TAB;
  readonly tabID: string;
}
interface SelectPageAction extends Action {
  readonly type: typeof PAGE_SELECT_TAB;
  readonly index: number;
}
interface GotoPageAction extends Action {
  readonly type: typeof PAGE_GO_TO;
  readonly tab: number;
  readonly target: number;
  readonly active: boolean;
}
interface OpenPopupAction extends Action {
  readonly type: typeof PAGE_OPEN_POPUP;
  readonly parent: string | null;
  readonly id: string;
  readonly zIndex: number;
  readonly path: string;
  readonly title: string;
  readonly modal: boolean;
  readonly resizable: boolean;
  readonly titlebar: boolean;
  readonly frame: boolean;
  readonly collapsed: boolean;
  readonly x?: number;
  readonly y?: number;
  readonly width?: number;
  readonly height?: number;
  readonly minWidth?: number;
  readonly minHeight?: number;
  readonly maxWidth?: number;
  readonly maxHeight?: number;

  // Internal use only
  readonly topLevel?: boolean;
  readonly needsRestoreCurrentTool?: boolean;
  readonly collapsible?: boolean;
  readonly initialMargin?: number;
}
interface ClosePopupAction extends Action {
  readonly type: typeof PAGE_CLOSE_POPUP;
  readonly id: string;
}
interface SetPopupCollapsedAction extends Action {
  readonly type: typeof PAGE_SET_POPUP_COLLAPSED;
  readonly id: string;
  readonly collapsed: boolean;
}
interface SetFocusAction extends Action {
  readonly type: typeof PAGE_SET_FOCUS;
  readonly focus: string;
}

interface GoToReferenceAction extends Action {
  readonly type: typeof PAGE_GO_TO_REFERENCE;
  readonly reference: string;
}

export type PageAction = (
  SimplePageAction | OpenMainTabAction | OpenTabAction | ReplaceContentAction |
  OpenReflowableAction | OpenGeneratedContentAction | ClosePageAction | SelectPageAction |
  GotoPageAction | OpenPopupAction | ClosePopupAction | SetPopupCollapsedAction |
  SetFocusAction | GoToReferenceAction
);

interface OpenPopupParams {
  readonly parent: string | null;
  readonly id: string;
  readonly zIndex: number;
  readonly path: string;
  readonly title: string;
  readonly modal: boolean;
  readonly resizable: boolean;
  readonly titlebar: boolean;
  readonly frame: boolean;
  readonly collapsed: boolean;
  readonly x?: number;
  readonly y?: number;
  readonly width?: number;
  readonly height?: number;
  readonly minWidth?: number;
  readonly minHeight?: number;
  readonly maxWidth?: number;
  readonly maxHeight?: number;

  // Internal use only
  readonly topLevel?: boolean;
  readonly needsRestoreCurrentTool?: boolean;
  readonly collapsible?: boolean;
  readonly initialMargin?: number;
}

export const showPrevPage = (_tab: number, dispatch: Dispatch<Action>): void => {
  dispatch(goToPrevPage());
};
export const showNextPage = (_tab: number, dispatch: Dispatch<Action>): void => {
  dispatch(goToNextPage());
};
export const moveToPage = (
  tab: number,
  index: number,
  dispatch: Dispatch<Action>,
  active?: boolean,
): void => {
  dispatch(goToPage(tab, index, active === undefined ? true : active));
};
export const openMainTab = (title: string, pageIndex: number): OpenMainTabAction => (
  { title, pageIndex, type: PAGE_OPEN_MAIN_TAB }
);
export const openNewTab = (
  path: string,
  title: string,
  favicon: string | null,
  active: boolean,
  key?: string,
): OpenTabAction => (
    { path, title, favicon, active, key, type: PAGE_OPEN_TAB }
  );
export const replaceContent =
  (path: string, title: string, favicon: string | null): ReplaceContentAction => (
    { path, title, favicon, type: PAGE_REPLACE_CONTENT }
  );
export const openReflowable = (title: string, pageIndex: number): OpenReflowableAction => (
  { title, pageIndex, type: PAGE_OPEN_REFLOWABLE }
);
export const openGeneratedContent =
  (key: string, title: string, xhtml: string, params: any = null)
    : OpenGeneratedContentAction => (
      { key, title, xhtml, params, type: PAGE_OPEN_GENERATED_CONTENT, location: 'tab' }
    );
export const closeTab = (tabID: string): ClosePageAction => (
  { tabID, type: PAGE_CLOSE_TAB }
);
export const selectTab = (index: number): SelectPageAction => (
  { index, type: PAGE_SELECT_TAB }
);
export const openPopup = (params: OpenPopupParams): OpenPopupAction => (
  { ...params, type: PAGE_OPEN_POPUP }
);
export const closePopup = (id: string): ClosePopupAction => ({ id, type: PAGE_CLOSE_POPUP });
export const setFocus = (focus: string): SetFocusAction => ({ focus, type: PAGE_SET_FOCUS });
export const setPopupCollapsed = (id: string, collapsed: boolean): SetPopupCollapsedAction => (
  { id, collapsed, type: PAGE_SET_POPUP_COLLAPSED }
);
export const goToReference = (reference: string): GoToReferenceAction => (
  { reference, type: PAGE_GO_TO_REFERENCE }
);

const goToPrevPage = (): SimplePageAction => ({ type: PAGE_GO_PREV });
const goToNextPage = (): SimplePageAction => ({ type: PAGE_GO_NEXT });
const goToPage = (tab: number, target: number, active: boolean): GotoPageAction => (
  { tab, target, active, type: PAGE_GO_TO }
);

export const isPageAction = (action: Action): action is PageAction => {
  switch (action.type) {
    case PAGE_OPEN_MAIN_TAB:
    case PAGE_GO_PREV:
    case PAGE_GO_NEXT:
    case PAGE_GO_TO:
    case PAGE_OPEN_TAB:
    case PAGE_REPLACE_CONTENT:
    case PAGE_CLOSE_TAB:
    case PAGE_OPEN_REFLOWABLE:
    case PAGE_OPEN_GENERATED_CONTENT:
    case PAGE_SELECT_TAB:
    case PAGE_OPEN_POPUP:
    case PAGE_CLOSE_POPUP:
    case PAGE_SET_POPUP_COLLAPSED:
    case PAGE_SET_FOCUS:
    case PAGE_GO_TO_REFERENCE:
      return true;
    default:
      return false;
  }
};
